<template>
  <v-app>
    <v-container>
      <v-row v-if="loading">
        <v-col cols="12" class="text-center">
          <v-progress-circular indeterminate color="primary"></v-progress-circular>
          <p class="mt-2">Loading requisition details...</p>
        </v-col>
      </v-row>
      <v-row v-else-if="!requisitionData">
        <v-col cols="12" class="text-center">
          <v-alert type="error">Requisition not found</v-alert>
          <v-btn class="mt-4" @click="$router.push({ name: 'money-requisition-record' })">
            Back to List
          </v-btn>
        </v-col>
      </v-row>
      <v-row v-else>
        <v-col cols="12">
          <div class="requisition-container" id="requisition-content">
            <!-- Header -->
            <div class="header">
              <h2>Money Requisition Details</h2>
              <div
                class="status"
                :class="{
                  approved: requisitionData.approval_status === 'Approved',
                  pending: requisitionData.approval_status === 'Pending',
                }"
              >
                {{ requisitionData.approval_status?.toUpperCase() || 'PENDING' }}
              </div>
            </div>

            <!-- Basic Info Table -->
            <table class="info-table">
              <tbody>
                <tr>
                    <td><strong>Requisition Code</strong></td>
                    <td>{{ requisitionData.code || 'N/A' }}</td>
                    <td><strong>Branch</strong></td>
                    <td>{{ branchName || 'N/A' }}</td>
                  </tr>
  
                <tr>
                    <td><strong>Employee</strong></td>
                    <td>{{ employeeName || 'N/A' }}</td>
                    <td><strong>Status</strong></td>
                    <td>{{ requisitionData.status == 1 ? 'Active' : 'Inactive' }}</td>
                  </tr>
                <tr>
                  <td><strong>Total Amount</strong></td>
                  <td>{{ formatCurrency(requisitionData.total_amount || 0) }}</td>
                  <td><strong>Disburse Amount</strong></td>
                  <td>{{ formatCurrency(requisitionData.disburse_amount || 0) }}</td>
                </tr>
                <tr>
                  <td><strong>Due Amount</strong></td>
                  <td>{{ formatCurrency(requisitionData.due_amount || 0) }}</td>
                </tr>
  
                <tr v-if="requisitionData.purpose">
                    <td><strong>Purpose</strong></td>
                    <td colspan="3">{{ requisitionData.purpose }}</td>
                  </tr>
              </tbody>
            </table>


            <!-- Details Table -->
            <h3>Requisition Items</h3>
            <table class="details-table">
              <thead>
                <tr>
                  <th style="width: 5%">SL</th>
                  <th>Description</th>
                  <th style="width: 20%" class="text-right">Amount</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="(detail, index) in requisitionData.details" :key="detail.id || index">
                  <td class="text-center">{{ index + 1 }}</td>
                  <td>{{ detail.description || 'N/A' }}</td>
                  <td class="text-right">{{ formatCurrency(detail.amount) }}</td>
                </tr>
                <tr v-if="!requisitionData.details || requisitionData.details.length === 0">
                  <td colspan="3" class="text-center" style="color: red;">
                    No item details found
                  </td>
                </tr>
              </tbody>
              <tfoot>
                <tr>
                  <td colspan="2" class="text-right"><strong>Total Amount</strong></td>
                  <td class="text-right">
                    <strong>{{ formatCurrency(requisitionData.total_amount || 0) }}</strong>
                  </td>
                </tr>
                <tr>
                  <td colspan="2" class="text-right"><strong>Disbursement Amount</strong></td>
                  <td class="text-right">
                    <strong>{{ formatCurrency(requisitionData.disburse_amount || 0) }}</strong>
                  </td>
                </tr>
                <tr>
                  <td colspan="2" class="text-right"><strong>Due Amount</strong></td>
                  <td class="text-right">
                    <strong>{{ formatCurrency(requisitionData.due_amount || 0) }}</strong>
                  </td>
                </tr>
              </tfoot>
            </table>

            <!-- Footer -->
            <div class="footer">
              <div>
               
                <strong>Created Date:</strong> {{ formatDateView(requisitionData.created_at) }}
                <template v-if="requisitionData.updated_at && requisitionData.updated_at !== requisitionData.created_at">
                  <br />
                  
                  <strong>Updated Date:</strong> {{ formatDateView(requisitionData.updated_at) }}
                </template>
              </div>
              <div class="signature">
                <p>______________________</p>
                <p>Authorized Signature</p>
              </div>
            </div>
          </div>

          <!-- Action Buttons -->
          <div class="action-buttons">
            <v-btn color="primary" class="mr-2" @click="printRequisition">
              <v-icon left>mdi-printer</v-icon> Print
            </v-btn>
          </div>
        </v-col>
      </v-row>
    </v-container>
  </v-app>
</template>

<script>
import { formatDateView, formatDateDash } from "@/plugins/helpers";
import { toast } from "vue3-toastify";

export default {
  data() {
    return {
      requisitionData: null,
      loading: false,
    };
  },
  computed: {
    getCurrentCompany() {
      return this.$store.getters.getCurrentCompany || {};
    },
    getFileServerUrl() {
      return this.$store.getters.getFileServerUrl || "";
    },
    getHrmEmployeeList() {
      return this.$store.getters.getHrmEmployeeList || [];
    },
    getCurrentBranch() {
      return this.$store.getters.getCurrentBranch || {};
    },
    employeeName() {
      if (!this.requisitionData || !this.requisitionData.employee_id) return "N/A";
      const employee = this.getHrmEmployeeList.find(
        (emp) => emp.id === this.requisitionData.employee_id
      );
      return employee
        ? `${employee.name}${employee.code || employee.employee_code ? ' - ' + (employee.code || employee.employee_code) : ''}`
        : this.requisitionData.employee?.name || "N/A";
    },
    branchName() {
      // Try to get from requisition data first, then fallback to current branch
      if (this.requisitionData?.branch?.name) {
        return this.requisitionData.branch.name;
      }
      if (this.getCurrentBranch && this.getCurrentBranch.name) {
        return this.getCurrentBranch.name;
      }
      return "N/A";
    },
    createdByName() {
      if (!this.requisitionData) return "N/A";
      // Get from requisition data relationship
      if (this.requisitionData.createdBy?.full_name) {
        return this.requisitionData.createdBy.full_name;
      }
      if (this.requisitionData.createdBy?.name) {
        return this.requisitionData.createdBy.name;
      }
      // Fallback to current user if available
      const currentUser = this.$store.getters.getAuthUser;
      if (currentUser && currentUser.name) {
        return currentUser.name;
      }
      return "N/A";
    },
    updatedByName() {
      if (!this.requisitionData) return "N/A";
      // Get from requisition data relationship
      if (this.requisitionData.updatedBy?.full_name) {
        return this.requisitionData.updatedBy.full_name;
      }
      if (this.requisitionData.updatedBy?.name) {
        return this.requisitionData.updatedBy.name;
      }
      return "N/A";
    },
    approverName() {
      if (!this.requisitionData) return "N/A";
      // Check if approval process data is available in response
      if (this.requisitionData.approvalCurrentProcess) {
        const approval = this.requisitionData.approvalCurrentProcess;
        if (approval.approvedBy?.full_name) {
          return approval.approvedBy.full_name;
        }
        if (approval.approvedBy?.name) {
          return approval.approvedBy.name;
        }
        if (approval.matrixDetail?.designation?.name) {
          return approval.matrixDetail.designation.name;
        }
      }
      // Check approvalProcess array if available
      if (this.requisitionData.approvalProcess && Array.isArray(this.requisitionData.approvalProcess)) {
        const approvedProcess = this.requisitionData.approvalProcess.find(ap => ap.approved_ind === 1 || ap.approved_ind === true);
        if (approvedProcess) {
          if (approvedProcess.approvedBy?.full_name) {
            return approvedProcess.approvedBy.full_name;
          }
          if (approvedProcess.approvedBy?.name) {
            return approvedProcess.approvedBy.name;
          }
          if (approvedProcess.matrixDetail?.designation?.name) {
            return approvedProcess.matrixDetail.designation.name;
          }
        }
      }
      return "N/A";
    },
  },
  methods: {
    formatDateView,
    formatCurrency(value) {
      return new Intl.NumberFormat("en-US", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      }).format(value || 0);
    },
    async loadRequisition() {
      const id = this.$route.params.id;

      if (!id) {
        toast.error("Requisition ID is required");
        this.$router.push({ name: "money-requisition-record" });
        return;
      }

      this.loading = true;
      try {
        // Load employee list if not loaded
        if (this.getHrmEmployeeList.length === 0) {
          await this.$store.dispatch("getHrmEmployeeList");
        }

        const response = await this.$store.dispatch("getMoneyRequisitionById", id);

        // Handle response structure
        let data = null;
        if (response && response.success && response.data) {
          if (Array.isArray(response.data) && response.data.length > 0) {
            data = response.data[0];
          } else if (response.data.id) {
            data = response.data;
          }
        } else if (response && response.data) {
          if (Array.isArray(response.data) && response.data.length > 0) {
            data = response.data[0];
          } else if (response.data.id) {
            data = response.data;
          }
        } else if (Array.isArray(response) && response.length > 0) {
          data = response[0];
        }

        if (data) {
          this.requisitionData = data;
        } else {
          toast.error("Requisition data not found");
          this.$router.push({ name: "money-requisition-record" });
        }
      } catch (error) {
        console.error("Error loading requisition:", error);
        toast.error(
          error.response?.data?.message || "Failed to load requisition details"
        );
        this.$router.push({ name: "money-requisition-record" });
      } finally {
        this.loading = false;
      }
    },
    printRequisition() {
      let printWindow = window.open(
        "",
        "PRINT",
        `width=${screen.width}, height=${screen.height}, left=0, top=0`
      );

      const company = this.getCurrentCompany;
      const fileServerUrl = this.getFileServerUrl;
      const statusClass =
        this.requisitionData.approval_status === "Approved" ? "approved" : "pending";
      const statusText =
        (this.requisitionData.approval_status || "PENDING").toUpperCase();

      const detailsRows = (this.requisitionData.details || [])
        .map(
          (detail, index) => `
        <tr>
          <td class="text-center">${index + 1}</td>
          <td>${detail.description || "N/A"}</td>
          <td class="text-right">${this.formatCurrency(detail.amount)}</td>
        </tr>
      `
        )
        .join("");

      printWindow.document.write(`
        <!DOCTYPE html>
        <html lang="en">
        <head>
          <meta charset="UTF-8">
          <meta name="viewport" content="width=device-width, initial-scale=1.0">
          <meta http-equiv="X-UA-Compatible" content="ie=edge">
          <title>Money Requisition Details</title>
          <link rel="preconnect" href="https://fonts.googleapis.com">
          <link rel="preconnect" href="https://fonts.gstatic.com" crossorigin>
          <link href="https://fonts.googleapis.com/css2?family=Arimo:ital,wght@0,400..700;1,400..700&display=swap" rel="stylesheet">
          <link href="https://cdn.jsdelivr.net/npm/bootstrap@5.3.8/dist/css/bootstrap.min.css" rel="stylesheet">
          <link href="https://cdn.jsdelivr.net/npm/@mdi/font@7.4.47/css/materialdesignicons.min.css" rel="stylesheet">
          <style>
            body { font-family: 'Arimo', sans-serif; margin: 0px; }
            .invoice-header { display:flex; justify-content:space-between; align-items:center; border-bottom:2px solid #eee; padding-bottom:10px; }
            .invoice-header img { max-height: 70px; max-width: 150px; }
            .table th, .table td { border: 1px solid #000; padding: 8px; vertical-align: middle; font-size: 12px; }
            .table th { background-color: #f0f0f0; color: #333; }
            .text-end { text-align: right; }
            .text-center { text-align: center; }
            h4 { font-size: 20px; color: #333; }
            p { font-size: 14px; color: #555; margin: 2px 0; }
            strong { font-weight: bold; color: #333; }
            @page { margin: 0.5in; size: letter; }
            .logo-container { display:flex; align-items:center; }
            .logo-container img { margin-left: 10px; }
            .table { width: 100%; border-collapse: collapse; table-layout: auto; }
            thead { display: table-header-group; }
            .info-table { width: 100%; border-collapse: collapse; margin-bottom: 20px; }
            .info-table td { padding: 8px; border: 1px solid #000; }
            .status { padding: 6px 14px; border-radius: 20px; font-size: 13px; font-weight: bold; color: #fff; display: inline-block; }
            .status.pending { background: #f0ad4e; }
            .status.approved { background: #5cb85c; }
            .footer { margin-top: 30px; display: flex; justify-content: space-between; }
            .signature { text-align: center; }
          </style>
        </head>
        <body>
          <div class="container-fluid position-relative">
            <div class="invoice-header">
              <div>
                <h4 class="text-uppercase">${company?.name || 'Company Name'}</h4>
                <p style="width:65%">${company?.address || ''}</p>
                <p><i class="mdi mdi-phone-outline"></i> ${company?.mobile || ''}</p>
                <p><i class="mdi mdi-email-outline"></i> ${company?.email || ''}</p>
                ${company?.website ? `<p><i class="mdi mdi-web"></i> ${company.website}</p>` : ''}
              </div>
              <div class="logo-container">
                ${company?.logo_thumb ? `<img src="${fileServerUrl}${company.logo_thumb}" alt="Logo" class="img-fluid">` : ''}
              </div>
            </div>
            <h4 class="text-center my-4"><strong>MONEY REQUISITION DETAILS</strong></h4>
            <div class="row mb-3">
              <div class="col-sm-8">
                <p><strong>Requisition Code:</strong> ${this.requisitionData.code || "N/A"}</p>
                <p><strong>Branch:</strong> ${this.branchName || "N/A"}</p>
                <p><strong>Employee:</strong> ${this.employeeName || "N/A"}</p>
                <p><strong>Total Amount:</strong> ${this.formatCurrency(this.requisitionData.total_amount || 0)}</p>
                <p><strong>Disburse Amount:</strong> ${this.formatCurrency(this.requisitionData.disburse_amount || 0)}</p>
                <p><strong>Due Amount:</strong> ${this.formatCurrency(this.requisitionData.due_amount || 0)}</p>
                ${this.requisitionData.purpose ? `<p><strong>Purpose:</strong> ${this.requisitionData.purpose}</p>` : ''}
              </div>
              <div class="col-sm-4 text-end">
                <p><strong>Status:</strong> ${this.requisitionData.status == 1 ? "Active" : "Inactive"}</p>
                <p><strong>Approval Status:</strong> <span class="status ${statusClass}">${statusText}</span></p>
                <p><strong>Total Records:</strong> ${(this.requisitionData.details || []).length}</p>
              </div>
            </div>
            <table class="table mt-4">
              <thead>
                <tr>
                  <th>SL</th>
                  <th>Description</th>
                  <th class="text-end">Amount</th>
                </tr>
              </thead>
              <tbody>
                ${detailsRows || '<tr><td colspan="3" class="text-center">No items found</td></tr>'}
                <tr style="background-color: #f5f5f5;">
                  <td colspan="2" class="text-end"><strong>Total Amount</strong></td>
                  <td class="text-end"><strong>${this.formatCurrency(this.requisitionData.total_amount || 0)}</strong></td>
                </tr>
                <tr style="background-color: #f5f5f5;">
                  <td colspan="2" class="text-end"><strong>Disbursement Amount</strong></td>
                  <td class="text-end"><strong>${this.formatCurrency(this.requisitionData.disburse_amount || 0)}</strong></td>
                </tr>
                <tr style="background-color: #f5f5f5;">
                  <td colspan="2" class="text-end"><strong>Due Amount</strong></td>
                  <td class="text-end"><strong>${this.formatCurrency(this.requisitionData.due_amount || 0)}</strong></td>
                </tr>
              </tbody>
            </table>
            <div class="footer">
              <div>
                <strong>Created Date:</strong> ${this.formatDateView(this.requisitionData.created_at)}
                ${this.requisitionData.updated_at && this.requisitionData.updated_at !== this.requisitionData.created_at ? `
                <br>
                <strong>Updated Date:</strong> ${this.formatDateView(this.requisitionData.updated_at)}
                ` : ""}
              </div>
              <div class="signature">
                <p>______________________</p>
                <p>Authorized Signature</p>
              </div>
            </div>
          </div>
        </body>
        </html>
      `);
      printWindow.moveTo(0, 0);
      printWindow.focus();
      setTimeout(() => {
        printWindow.print();
        printWindow.close();
      }, 500);
    },
  },
  created() {
    this.loadRequisition();
  },
};
</script>

<style scoped>
.requisition-container {
  max-width: 900px;
  background: #fff;
  margin: auto;
  padding: 25px;
  border-radius: 6px;
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.08);
}

.header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  border-bottom: 2px solid #ddd;
  padding-bottom: 10px;
  margin-bottom: 20px;
}

.header h2 {
  margin: 0;
  color: #333;
}

.status {
  padding: 6px 14px;
  border-radius: 20px;
  font-size: 13px;
  font-weight: bold;
  color: #fff;
}

.status.pending {
  background: #f0ad4e;
}

.status.approved {
  background: #5cb85c;
}

.info-table {
  width: 100%;
  border-collapse: collapse;
  margin-bottom: 20px;
}

.info-table td {
  padding: 8px;
  border: 1px solid #ddd;
}

.details-table {
  width: 100%;
  border-collapse: collapse;
}

.details-table th,
.details-table td {
  border: 1px solid #ddd;
  padding: 8px;
}

.details-table thead {
  background: #f1f3f6;
}

.text-right {
  text-align: right;
}

.text-center {
  text-align: center;
}

.details-table tfoot td {
  background: #fafafa;
  font-weight: bold;
}

h3 {
  color: #333;
  margin-top: 20px;
  margin-bottom: 10px;
}

.footer {
  margin-top: 30px;
  display: flex;
  justify-content: space-between;
}

.signature {
  text-align: center;
}

.action-buttons {
  text-align: center;
  margin-top: 20px;
  padding: 20px;
}

@media print {
  .action-buttons {
    display: none;
  }
}
</style>
